import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface Citation {
  rule_id: string;
  rule_name: string;
  rule_status: string;
  section_path: string;
  verbatim_quote: string;
  manual_page: number | null;
  pdf_page: number | null;
  role: string;
  order: number;
}

interface CitedRule {
  rule_id: string;
  name: string;
  status: string;
  archetype: string;
  jurisdiction: string;
  current_version: number;
}

interface Source {
  source_id: string;
  display_name: string;
  type: string;
  authority: string;
  status: string;
  release_date: string | null;
  last_verified: string | null;
  superseded_by: string | null;
  notes: string;
  citations: Citation[];
  cited_by_rules: CitedRule[];
  created_at: string;
  updated_at: string;
}

const StatuteDetail = ({ sourceId }: { sourceId: string }) => {
  const navigate = useNavigate();
  const [source, setSource] = useState<Source | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSource(null);
    api
      .get(`/explore/sources/${sourceId}/`)
      .then((res) => { if (!cancelled) setSource(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sourceId]);

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !source) {
    return <Box sx={{ p: 4 }}><Typography color="error">{error || 'Source not found'}</Typography></Box>;
  }

  return (
    <Box>
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {source.display_name || source.source_id}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {source.source_id}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={source.type} size="small" variant="outlined" />
          {source.authority && <Chip label={source.authority} size="small" variant="outlined" />}
          <Chip
            label={source.status}
            size="small"
            color={source.status === 'current' ? 'success' : 'default'}
          />
          {source.release_date && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              released {source.release_date}
            </Typography>
          )}
        </Box>
        {source.superseded_by && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            superseded by{' '}
            <Box
              component="span"
              onClick={() => navigate(`/explore/statutes/${source.superseded_by}`)}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {source.superseded_by}
            </Box>
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 920 }}>
          {source.notes && (
            <Section title="Notes">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{source.notes}</Typography>
            </Section>
          )}

          <Section title={`Cited by rules (${source.cited_by_rules.length})`}>
            {source.cited_by_rules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No rules cite this statute yet.
              </Typography>
            ) : (
              <Paper variant="outlined">
                {source.cited_by_rules.map((r, i) => (
                  <Box
                    key={r.rule_id}
                    onClick={() => navigate(`/explore/rules/${r.rule_id}`)}
                    sx={{
                      px: 2, py: 1.5,
                      borderBottom: i < source.cited_by_rules.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.name || r.rule_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.rule_id} · v{r.current_version}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={r.status} size="small" color={ruleStatusColor(r.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
                        {r.archetype && (
                          <Chip label={r.archetype} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
          </Section>

          <Section title={`Citation excerpts (${source.citations.length})`}>
            {source.citations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No verbatim quotes pinned to this source yet.
              </Typography>
            ) : (
              source.citations.map((c, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {c.section_path || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Chip label={c.role} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                      <Typography variant="caption" color="text.secondary">
                        {c.manual_page ? `p. ${c.manual_page}` : ''}
                        {c.manual_page && c.pdf_page ? ' · ' : ''}
                        {c.pdf_page ? `pdf p. ${c.pdf_page}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                    "{c.verbatim_quote}"
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="caption"
                    color="primary"
                    onClick={() => navigate(`/explore/rules/${c.rule_id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    cited by {c.rule_id}
                  </Typography>
                </Paper>
              ))
            )}
          </Section>
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

function ruleStatusColor(s: string): 'default' | 'success' | 'warning' | 'error' {
  switch (s) {
    case 'active': return 'success';
    case 'proposed': return 'warning';
    case 'deprecated': return 'error';
    default: return 'default';
  }
}

export default StatuteDetail;
